import { useCallback, useEffect, useState } from "react";
import { Copy, Mail, MessageCircle, QrCode, RefreshCw, Send, Settings, Tablet, Trash2, Clock, Palmtree, Plus, Bell, Cake, Smartphone, ClipboardCheck, Receipt, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { diasPorModoVacaciones } from "@/lib/format";
import { defaultKioskApiUrl } from "@/lib/kioskQr";
import PageHeader from "@/components/PageHeader";
import KioskQrCode from "@/components/KioskQrCode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import GuiaPlantillasFichajeWhatsApp from "@/components/GuiaPlantillasFichajeWhatsApp";
import GuiaFichajeTelegram from "@/components/GuiaFichajeTelegram";
import GuiaTelegram from "@/components/GuiaTelegram";
import AjustesSeccion, { AjustesSubbloque } from "@/components/AjustesSeccion";
import { useEmpresa } from "@/hooks/useEmpresa";

const VACIO = {
  smtp_host: "", smtp_port: 587, smtp_user: "", smtp_password: "",
  sender_email: "", sender_name: "Ventura HR", use_tls: true, activo: false,
};

const WA_VACIO = {
  activo: false,
  phone_number_id: "",
  access_token: "",
  waba_id: "",
  webhook_verify_token: "",
  graph_api_version: "v21.0",
  webhook_url: "/api/webhooks/whatsapp",
};

const ALTIRIA_VACIO = {
  activo_sms: false,
  activo_whatsapp: false,
  api_usuario: "",
  api_password: "",
  api_host: "",
  remitente_sms: "",
  remitente_whatsapp: "",
  plantilla_whatsapp_id: "",
};

const TG_VACIO = {
  activo: false,
  bot_token: "",
  bot_username: "",
  webhook_secret: "",
  webhook_url: "/api/webhooks/telegram",
};

export default function Ajustes() {
  const { empresa } = useEmpresa();
  const gerenciaModulo = !!empresa?.gestionar_gerencia;
  const [form, setForm] = useState(VACIO);
  const [waForm, setWaForm] = useState(WA_VACIO);
  const [waEstado, setWaEstado] = useState({ status: "disabled", message: "" });
  const [waTokenGuardado, setWaTokenGuardado] = useState(false);
  const [waVerifyGuardado, setWaVerifyGuardado] = useState(false);
  const [guardandoWa, setGuardandoWa] = useState(false);
  const [probandoWa, setProbandoWa] = useState(false);
  const [tgForm, setTgForm] = useState(TG_VACIO);
  const [tgEstado, setTgEstado] = useState({ status: "disabled", message: "" });
  const [tgTokenGuardado, setTgTokenGuardado] = useState(false);
  const [guardandoTg, setGuardandoTg] = useState(false);
  const [probandoTg, setProbandoTg] = useState(false);
  const [registrandoWebhookTg, setRegistrandoWebhookTg] = useState(false);
  const [altiriaForm, setAltiriaForm] = useState(ALTIRIA_VACIO);
  const [altiriaPasswordGuardada, setAltiriaPasswordGuardada] = useState(false);
  const [guardandoAltiria, setGuardandoAltiria] = useState(false);
  const [probandoAltiriaSms, setProbandoAltiriaSms] = useState(false);
  const [probandoAltiriaWa, setProbandoAltiriaWa] = useState(false);
  const [verificandoAltiria, setVerificandoAltiria] = useState(false);
  const [notifCatalogo, setNotifCatalogo] = useState([]);
  const [notifEventos, setNotifEventos] = useState({});
  const [guardandoNotif, setGuardandoNotif] = useState(false);
  const [aprobCatalogo, setAprobCatalogo] = useState([]);
  const [aprobConfig, setAprobConfig] = useState({});
  const [guardandoAprob, setGuardandoAprob] = useState(false);
  const [passwordGuardada, setPasswordGuardada] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [probando, setProbando] = useState(false);
  const [kioskToken, setKioskToken] = useState(null);
  const [kioskApiUrl, setKioskApiUrl] = useState(() => defaultKioskApiUrl());
  const [empresaCfg, setEmpresaCfg] = useState({
    usar_sistema_cortesia: true,
    tiempo_cortesia_minutos: 15,
    pausa_cafe_minutos: 15,
    vacaciones_modo: "naturales",
    dias_vacaciones: 30,
    vacaciones_arrastre_activo: true,
    vacaciones_arrastre_max: "",
    vacaciones_arrastre_permite_negativo: true,
    dias_asuntos_propios: 0,
    asuntos_propios_activo: false,
    recordatorio_manuales_activo: false,
    recordatorio_manuales_umbral: 3,
    recordatorio_manuales_dias: 14,
    recordatorio_manuales_cooldown_dias: 7,
    recordatorio_manuales_mensaje: "",
    autocierre_jornada_activo: false,
    autocierre_jornada_minutos_gracia: 30,
    festivo_domingo_a_lunes: true,
    cumpleanos_activo: false,
    cumpleanos_mensaje: "",
    cumpleanos_equipo_mensaje: "",
    fichaje_whatsapp_activo: false,
    fichaje_whatsapp_minutos_despues: 15,
    fichaje_whatsapp_solo_sin_picados: true,
    fichaje_whatsapp_plantilla_meta: "",
    fichaje_whatsapp_plantilla_altiria_id: "",
    fichaje_whatsapp_base_url: "",
    fichaje_whatsapp_modo_picado: "automatico",
    fichaje_telegram_activo: false,
    fichaje_telegram_minutos_despues: 15,
    fichaje_telegram_solo_sin_picados: true,
    fichaje_telegram_modo_picado: "automatico",
    gastos_tarifa_km: "0,26",
    gastos_radio_dieta_metros: 500,
    gastos_margen_desviacion_km: "10",
    hora_extra_umbral_minutos: 15,
  });
  const [sistemaCfg, setSistemaCfg] = useState({ tiempo_cortesia_minutos: 15, pausa_cafe_minutos: 15 });
  const [guardandoEmpresa, setGuardandoEmpresa] = useState(false);
  const [guardandoGastos, setGuardandoGastos] = useState(false);
  const [motivosIncidencia, setMotivosIncidencia] = useState([]);
  const [motivosAusencia, setMotivosAusencia] = useState([]);
  const [motivosPausa, setMotivosPausa] = useState([]);
  const [motivoNuevo, setMotivoNuevo] = useState({ incidencia: "", ausencia_justificada: "", pausa: "" });
  const [editandoMotivo, setEditandoMotivo] = useState(null);

  const cargarMotivos = useCallback(async () => {
    try {
      const [inc, aus, pau] = await Promise.all([
        api.get("/motivos-incidencia", { params: { solo_activos: false, ambito: "incidencia" } }),
        api.get("/motivos-incidencia", { params: { solo_activos: false, ambito: "ausencia_justificada" } }),
        api.get("/motivos-incidencia", { params: { solo_activos: false, ambito: "pausa" } }),
      ]);
      setMotivosIncidencia(inc.data || []);
      setMotivosAusencia(aus.data || []);
      setMotivosPausa(pau.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  const guardarMotivoEdit = async () => {
    if (!editandoMotivo?.id || !editandoMotivo.nombre?.trim()) return;
    try {
      await api.put(`/motivos-incidencia/${editandoMotivo.id}`, { nombre: editandoMotivo.nombre.trim() });
      setEditandoMotivo(null);
      cargarMotivos();
      toast.success("Motivo actualizado");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const anadirMotivo = async (ambito) => {
    const nombre = (motivoNuevo[ambito] || "").trim();
    if (nombre.length < 2) return;
    const lista =
      ambito === "incidencia" ? motivosIncidencia : ambito === "pausa" ? motivosPausa : motivosAusencia;
    try {
      await api.post("/motivos-incidencia", {
        nombre,
        es_otros: false,
        orden: lista.filter((m) => m.activo).length + 1,
        ambito,
      });
      setMotivoNuevo((prev) => ({ ...prev, [ambito]: "" }));
      cargarMotivos();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const cargar = useCallback(async () => {
    try {
      const [c, k, emp] = await Promise.all([
        api.get("/ajustes/correo"),
        api.get("/ajustes/quiosco"),
        api.get("/ajustes/empresa"),
      ]);
      setForm({ ...VACIO, ...c.data, smtp_password: "" });
      setPasswordGuardada(c.data.password_guardada);
      setKioskToken(k.data.token);
      if (k.data.api_url) {
        setKioskApiUrl(k.data.api_url);
      } else {
        setKioskApiUrl(defaultKioskApiUrl());
      }
      setSistemaCfg(emp.data.sistema);
      const e = emp.data.empresa;
      setEmpresaCfg({
        usar_sistema_cortesia: e.usar_sistema_cortesia,
        tiempo_cortesia_minutos: e.tiempo_cortesia_minutos ?? emp.data.sistema.tiempo_cortesia_minutos,
        pausa_cafe_minutos: e.pausa_cafe_minutos ?? emp.data.sistema.pausa_cafe_minutos,
        vacaciones_modo: e.vacaciones_modo || "naturales",
        dias_vacaciones: e.dias_vacaciones ?? e.efectivo?.dias_vacaciones ?? diasPorModoVacaciones(e.vacaciones_modo),
        vacaciones_arrastre_activo: e.vacaciones_arrastre_activo ?? true,
        vacaciones_arrastre_max: e.vacaciones_arrastre_max ?? "",
        vacaciones_arrastre_permite_negativo: e.vacaciones_arrastre_permite_negativo ?? true,
        dias_asuntos_propios: e.dias_asuntos_propios ?? e.efectivo?.dias_asuntos_propios ?? 0,
        asuntos_propios_activo: e.asuntos_propios_activo ?? e.efectivo?.asuntos_propios_activo ?? false,
        recordatorio_manuales_activo: e.recordatorio_manuales_activo ?? false,
        recordatorio_manuales_umbral: e.recordatorio_manuales_umbral ?? 3,
        recordatorio_manuales_dias: e.recordatorio_manuales_dias ?? 14,
        recordatorio_manuales_cooldown_dias: e.recordatorio_manuales_cooldown_dias ?? 7,
        recordatorio_manuales_mensaje: e.recordatorio_manuales_mensaje || "",
        autocierre_jornada_activo: e.autocierre_jornada_activo ?? false,
        autocierre_jornada_minutos_gracia: e.autocierre_jornada_minutos_gracia ?? 30,
        festivo_domingo_a_lunes: e.festivo_domingo_a_lunes ?? true,
        cumpleanos_activo: e.cumpleanos_activo ?? false,
        cumpleanos_mensaje: e.cumpleanos_mensaje || "",
        cumpleanos_equipo_mensaje: e.cumpleanos_equipo_mensaje || "",
        fichaje_whatsapp_activo: e.fichaje_whatsapp_activo ?? false,
        fichaje_whatsapp_minutos_despues: e.fichaje_whatsapp_minutos_despues ?? 15,
        fichaje_whatsapp_solo_sin_picados: e.fichaje_whatsapp_solo_sin_picados ?? true,
        fichaje_whatsapp_plantilla_meta: e.fichaje_whatsapp_plantilla_meta || "",
        fichaje_whatsapp_plantilla_altiria_id: e.fichaje_whatsapp_plantilla_altiria_id ?? "",
        fichaje_whatsapp_base_url: e.fichaje_whatsapp_base_url || "",
        fichaje_whatsapp_modo_picado:
          e.fichaje_whatsapp_modo_picado === "manual" ? "manual" : "automatico",
        fichaje_telegram_activo: e.fichaje_telegram_activo ?? false,
        fichaje_telegram_minutos_despues: e.fichaje_telegram_minutos_despues ?? 15,
        fichaje_telegram_solo_sin_picados: e.fichaje_telegram_solo_sin_picados ?? true,
        fichaje_telegram_modo_picado:
          e.fichaje_telegram_modo_picado === "manual" ? "manual" : "automatico",
        gastos_tarifa_km: String(e.gastos_tarifa_km ?? e.efectivo?.gastos_tarifa_km ?? 0.26).replace(".", ","),
        gastos_radio_dieta_metros: e.gastos_radio_dieta_metros ?? e.efectivo?.gastos_radio_dieta_metros ?? 500,
        gastos_margen_desviacion_km: String(e.gastos_margen_desviacion_km ?? e.efectivo?.gastos_margen_desviacion_km ?? 10).replace(".", ","),
        hora_extra_umbral_minutos: e.hora_extra_umbral_minutos ?? e.efectivo?.hora_extra_umbral_minutos ?? 15,
      });
    } catch (e) {
      toast.error(formatApiError(e));
    }
    try {
      const { data: wa } = await api.get("/ajustes/whatsapp");
      setWaForm({ ...WA_VACIO, ...wa, access_token: "", webhook_verify_token: "" });
      setWaTokenGuardado(!!wa?.token_guardado);
      setWaVerifyGuardado(!!wa?.verify_token_guardado);
      setWaEstado(wa?.estado || { status: "disabled", message: "" });
    } catch (e) {
      console.warn("WhatsApp ajustes no disponibles", e);
    }
    try {
      const { data: tg } = await api.get("/ajustes/telegram");
      setTgForm({
        ...TG_VACIO,
        activo: !!tg?.activo,
        bot_token: "",
        bot_username: tg?.bot_username || "",
        webhook_secret: "",
        webhook_url: tg?.webhook_url || TG_VACIO.webhook_url,
      });
      setTgTokenGuardado(!!tg?.bot_token_guardado);
      setTgEstado(tg?.estado || { status: "disabled", message: "" });
    } catch (e) {
      console.warn("Telegram ajustes no disponibles", e);
    }
    try {
      const { data: alt } = await api.get("/ajustes/altiria");
      setAltiriaForm({
        ...ALTIRIA_VACIO,
        ...alt,
        api_password: "",
        plantilla_whatsapp_id: alt.plantilla_whatsapp_id ?? "",
      });
      setAltiriaPasswordGuardada(!!alt?.password_guardada);
    } catch (e) {
      console.warn("Altiria ajustes no disponibles", e);
    }
    try {
      const { data: nc } = await api.get("/ajustes/notificaciones-canales");
      setNotifCatalogo(nc.catalogo || []);
      setNotifEventos(nc.eventos || {});
    } catch (e) {
      console.warn("Notificaciones canales no disponibles", e);
    }
    try {
      const { data: ap } = await api.get("/ajustes/solicitudes-aprobacion");
      setAprobCatalogo(ap.catalogo || []);
      setAprobConfig(ap.config || {});
    } catch (e) {
      console.warn("Aprobación de solicitudes no disponible", e);
    }
  }, []);

  useEffect(() => {
    cargar();
    cargarMotivos();
  }, [cargar, cargarMotivos]);

  const guardar = async () => {
    setGuardando(true);
    try {
      const { data } = await api.put("/ajustes/correo", { ...form, smtp_port: Number(form.smtp_port) });
      setPasswordGuardada(data.password_guardada);
      setForm((f) => ({ ...f, smtp_password: "" }));
      toast.success("Configuración de correo guardada");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardando(false);
    }
  };

  const probar = async () => {
    setProbando(true);
    try {
      const { data } = await api.post("/ajustes/correo/test");
      toast.success(data.mensaje);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setProbando(false);
    }
  };

  const guardarWhatsApp = async () => {
    setGuardandoWa(true);
    try {
      const { data } = await api.put("/ajustes/whatsapp", waForm);
      setWaTokenGuardado(data.token_guardado);
      setWaVerifyGuardado(data.verify_token_guardado);
      setWaForm((f) => ({ ...f, access_token: "", webhook_verify_token: "" }));
      toast.success("Configuración WhatsApp guardada");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardandoWa(false);
    }
  };

  const refrescarEstadoWa = async () => {
    try {
      const { data } = await api.get("/ajustes/whatsapp/estado");
      setWaEstado(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const probarWhatsApp = async () => {
    setProbandoWa(true);
    try {
      const { data } = await api.post("/ajustes/whatsapp/test");
      toast.success(data.mensaje);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setProbandoWa(false);
    }
  };

  const guardarTelegram = async () => {
    setGuardandoTg(true);
    try {
      const { data } = await api.put("/ajustes/telegram", {
        activo: tgForm.activo,
        bot_token: tgForm.bot_token,
        bot_username: tgForm.bot_username,
        webhook_secret: tgForm.webhook_secret,
      });
      setTgTokenGuardado(!!data.bot_token_guardado);
      setTgForm((f) => ({
        ...f,
        bot_token: "",
        webhook_secret: "",
        bot_username: data.bot_username || f.bot_username,
        webhook_url: data.webhook_url || f.webhook_url,
        activo: !!data.activo,
      }));
      setTgEstado(data.estado || { status: "disabled", message: "" });
      toast.success("Configuración Telegram guardada");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardandoTg(false);
    }
  };

  const refrescarEstadoTg = async () => {
    try {
      const { data } = await api.get("/ajustes/telegram/estado");
      setTgEstado(data);
      if (data?.bot_username) {
        setTgForm((f) => ({ ...f, bot_username: data.bot_username }));
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const registrarWebhookTg = async () => {
    setRegistrandoWebhookTg(true);
    try {
      const { data } = await api.post("/ajustes/telegram/registrar-webhook");
      toast.success(data.mensaje);
      if (data.webhook_url) {
        setTgForm((f) => ({ ...f, webhook_url: data.webhook_url }));
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRegistrandoWebhookTg(false);
    }
  };

  const probarTelegram = async () => {
    setProbandoTg(true);
    try {
      const { data } = await api.post("/ajustes/telegram/test");
      toast.success(data.mensaje);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setProbandoTg(false);
    }
  };

  const guardarAltiria = async () => {
    setGuardandoAltiria(true);
    try {
      const payload = {
        ...altiriaForm,
        plantilla_whatsapp_id: altiriaForm.plantilla_whatsapp_id === "" || altiriaForm.plantilla_whatsapp_id == null
          ? null
          : Number(altiriaForm.plantilla_whatsapp_id),
      };
      const { data } = await api.put("/ajustes/altiria", payload);
      setAltiriaPasswordGuardada(data.password_guardada);
      setAltiriaForm((f) => ({ ...f, api_password: "" }));
      toast.success("Configuración Altiria guardada");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardandoAltiria(false);
    }
  };

  const probarAltiriaSms = async () => {
    setProbandoAltiriaSms(true);
    try {
      const { data } = await api.post("/ajustes/altiria/test-sms");
      toast.success(data.mensaje);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setProbandoAltiriaSms(false);
    }
  };

  const probarAltiriaWhatsapp = async () => {
    setProbandoAltiriaWa(true);
    try {
      const { data } = await api.post("/ajustes/altiria/test-whatsapp");
      toast.success(data.mensaje);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setProbandoAltiriaWa(false);
    }
  };

  const verificarCuentaAltiria = async () => {
    setVerificandoAltiria(true);
    try {
      const { data } = await api.post("/ajustes/altiria/verificar-cuenta");
      const c = data.cuenta || {};
      const saldo = c.saldo != null ? ` · Saldo: ${c.saldo}${c.currency ? ` ${c.currency}` : ""}` : "";
      toast.success(`${data.mensaje || "Cuenta OK"}${c.username ? ` (${c.username})` : ""}${saldo}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setVerificandoAltiria(false);
    }
  };

  const toggleNotifCanal = (eventoId, canal) => {
    setNotifEventos((prev) => ({
      ...prev,
      [eventoId]: {
        ...prev[eventoId],
        [canal]: !prev[eventoId]?.[canal],
      },
    }));
  };

  const guardarNotificacionesCanales = async () => {
    setGuardandoNotif(true);
    try {
      const { data } = await api.put("/ajustes/notificaciones-canales", { eventos: notifEventos });
      setNotifEventos(data.eventos || notifEventos);
      toast.success("Preferencias de canales guardadas");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardandoNotif(false);
    }
  };

  const toggleAprobRol = (grupoId, campo, rol) => {
    setAprobConfig((prev) => {
      const actual = prev[grupoId] || { aprobadores: [], notificar: [], gerencia: false };
      const roles = actual[campo] || [];
      const activo = roles.includes(rol);
      let nuevos = activo ? roles.filter((r) => r !== rol) : [...roles, rol];
      if (nuevos.length === 0) {
        toast.error("Debe quedar al menos un rol seleccionado");
        return prev;
      }
      return { ...prev, [grupoId]: { ...actual, [campo]: nuevos } };
    });
  };

  const toggleAprobGerencia = (grupoId) => {
    setAprobConfig((prev) => {
      const actual = prev[grupoId] || { aprobadores: [], notificar: [], gerencia: false };
      return { ...prev, [grupoId]: { ...actual, gerencia: !actual.gerencia } };
    });
  };

  const guardarSolicitudesAprobacion = async () => {
    setGuardandoAprob(true);
    try {
      const { data } = await api.put("/ajustes/solicitudes-aprobacion", { config: aprobConfig });
      setAprobConfig(data.config || aprobConfig);
      toast.success("Configuración de aprobaciones guardada");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardandoAprob(false);
    }
  };

  const guardarEmpresa = async () => {
    setGuardandoEmpresa(true);
    try {
      await api.put("/ajustes/empresa", {
        usar_sistema_cortesia: empresaCfg.usar_sistema_cortesia,
        tiempo_cortesia_minutos: empresaCfg.usar_sistema_cortesia ? null : Number(empresaCfg.tiempo_cortesia_minutos),
        pausa_cafe_minutos: empresaCfg.usar_sistema_cortesia ? null : Number(empresaCfg.pausa_cafe_minutos),
        vacaciones_modo: empresaCfg.vacaciones_modo,
        dias_vacaciones: Number(empresaCfg.dias_vacaciones),
        vacaciones_arrastre_activo: !!empresaCfg.vacaciones_arrastre_activo,
        vacaciones_arrastre_max:
          empresaCfg.vacaciones_arrastre_max === "" || empresaCfg.vacaciones_arrastre_max == null
            ? null
            : Number(empresaCfg.vacaciones_arrastre_max),
        vacaciones_arrastre_permite_negativo: !!empresaCfg.vacaciones_arrastre_permite_negativo,
        dias_asuntos_propios: Number(empresaCfg.dias_asuntos_propios),
        asuntos_propios_activo: empresaCfg.asuntos_propios_activo,
        recordatorio_manuales_activo: empresaCfg.recordatorio_manuales_activo,
        recordatorio_manuales_umbral: Number(empresaCfg.recordatorio_manuales_umbral),
        recordatorio_manuales_dias: Number(empresaCfg.recordatorio_manuales_dias),
        recordatorio_manuales_cooldown_dias: Number(empresaCfg.recordatorio_manuales_cooldown_dias),
        recordatorio_manuales_mensaje: empresaCfg.recordatorio_manuales_mensaje,
        autocierre_jornada_activo: empresaCfg.autocierre_jornada_activo,
        autocierre_jornada_minutos_gracia: Number(empresaCfg.autocierre_jornada_minutos_gracia),
        festivo_domingo_a_lunes: !!empresaCfg.festivo_domingo_a_lunes,
        cumpleanos_activo: empresaCfg.cumpleanos_activo,
        cumpleanos_mensaje: empresaCfg.cumpleanos_mensaje,
        cumpleanos_equipo_mensaje: empresaCfg.cumpleanos_equipo_mensaje,
        fichaje_whatsapp_activo: !!empresaCfg.fichaje_whatsapp_activo,
        fichaje_whatsapp_minutos_despues: Number(empresaCfg.fichaje_whatsapp_minutos_despues),
        fichaje_whatsapp_solo_sin_picados: !!empresaCfg.fichaje_whatsapp_solo_sin_picados,
        fichaje_whatsapp_plantilla_meta: empresaCfg.fichaje_whatsapp_plantilla_meta || "",
        fichaje_whatsapp_plantilla_altiria_id:
          empresaCfg.fichaje_whatsapp_plantilla_altiria_id === "" ||
          empresaCfg.fichaje_whatsapp_plantilla_altiria_id == null
            ? null
            : Number(empresaCfg.fichaje_whatsapp_plantilla_altiria_id),
        fichaje_whatsapp_base_url: (empresaCfg.fichaje_whatsapp_base_url || "").trim(),
        fichaje_whatsapp_modo_picado:
          empresaCfg.fichaje_whatsapp_modo_picado === "manual" ? "manual" : "automatico",
        fichaje_telegram_activo: !!empresaCfg.fichaje_telegram_activo,
        fichaje_telegram_minutos_despues: Number(empresaCfg.fichaje_telegram_minutos_despues),
        fichaje_telegram_solo_sin_picados: !!empresaCfg.fichaje_telegram_solo_sin_picados,
        fichaje_telegram_modo_picado:
          empresaCfg.fichaje_telegram_modo_picado === "manual" ? "manual" : "automatico",
        hora_extra_umbral_minutos: Number(empresaCfg.hora_extra_umbral_minutos ?? 15),
      });
      toast.success("Configuración de jornada guardada");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardandoEmpresa(false);
    }
  };

  const guardarGastos = async () => {
    setGuardandoGastos(true);
    try {
      await api.put("/ajustes/empresa", {
        gastos_tarifa_km: Number(String(empresaCfg.gastos_tarifa_km).replace(",", ".")),
        gastos_radio_dieta_metros: Number(empresaCfg.gastos_radio_dieta_metros),
        gastos_margen_desviacion_km: Number(String(empresaCfg.gastos_margen_desviacion_km).replace(",", ".")),
      });
      toast.success("Configuración de gastos guardada");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardandoGastos(false);
    }
  };

  const kioskUrl = kioskToken ? `${window.location.origin}/quiosco/${kioskToken}` : null;

  const activarQuiosco = async () => {
    try {
      const { data } = await api.post("/ajustes/quiosco/regenerar");
      setKioskToken(data.token);
      toast.success("Modo quiosco activado: comparte el enlace con la tablet");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const desactivarQuiosco = async () => {
    if (!window.confirm("¿Desactivar el modo quiosco? El enlace actual dejará de funcionar.")) return;
    try {
      await api.delete("/ajustes/quiosco");
      setKioskToken(null);
      toast.success("Modo quiosco desactivado");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copiarUrl = async () => {
    try {
      await navigator.clipboard.writeText(kioskUrl);
      toast.success("Enlace copiado");
    } catch (e) {
      toast.error("No se pudo copiar el enlace");
    }
  };

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6" data-testid="ajustes-page">
      <PageHeader
        icon={Settings}
        title="Ajustes"
        subtitle="Correo, SMS, WhatsApp, Telegram, jornada, vacaciones y modo quiosco"
        sectionKey="ajustes"
        brandGradient={false}
      />

      <nav className="space-y-3 text-sm">
        <div>
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">Jornada</p>
          <div className="flex flex-wrap gap-2">
            <a href="#jornada-config" className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-violet-800 hover:bg-violet-100">Jornada y vacaciones</a>
            <a href="#fichaje-whatsapp" className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-emerald-800 hover:bg-emerald-100">Fichar WhatsApp</a>
            <a href="#fichaje-telegram" className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 text-sky-800 hover:bg-sky-100">Fichar Telegram</a>
          </div>
        </div>
        <div>
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">Comunicación</p>
          <div className="flex flex-wrap gap-2">
            <a href="#correo-smtp" className="rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-zinc-600 hover:border-indigo-200 hover:text-indigo-700">Correo SMTP</a>
            <a href="#whatsapp-config" className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-emerald-800 hover:bg-emerald-100">WhatsApp Meta</a>
            <a href="#telegram-config" className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 text-sky-800 hover:bg-sky-100">Telegram bot</a>
            <a href="#altiria-config" className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 text-sky-800 hover:bg-sky-100">Altiria SMS/WA</a>
          </div>
        </div>
        <div>
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">Avisos y reglas</p>
          <div className="flex flex-wrap gap-2">
            <a href="#notificaciones-canales" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-amber-800 hover:bg-amber-100">Canales por evento</a>
            <a href="#solicitudes-aprobacion" className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-emerald-800 hover:bg-emerald-100">Aprobación</a>
            <a href="#gastos-config" className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-emerald-800 hover:bg-emerald-100">Gastos</a>
            <a href="#quiosco-config" className="rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-zinc-600 hover:border-indigo-200 hover:text-indigo-700">Quiosco</a>
          </div>
        </div>
      </nav>

      <div id="jornada-config" className="scroll-mt-4 space-y-3">
      <AjustesSeccion
        id="jornada-card"
        title="Jornada y vacaciones"
        subtitle="Cortesía, vacaciones, avisos de fichaje y cumpleaños"
        icon={Clock}
        iconClass="bg-gradient-to-br from-violet-500 to-purple-600"
        borderClass="border-violet-100"
        defaultOpen
      >
        <div className="space-y-3">
          <AjustesSubbloque
            id="jornada-cortesia"
            title="Cortesía, pausas y horas extra"
            subtitle="Tiempos de cortesía, pausa reconocida y umbral de horas extra"
            icon={Clock}
            iconColor="text-violet-600"
            borderClass="border-violet-100 bg-violet-50/30"
            defaultOpen
          >
          <div className="flex items-center justify-between rounded-lg border border-zinc-100 bg-white px-4 py-3">
            <div>
              <p className="text-sm font-medium">Usar configuración global de cortesía y pausas</p>
              <p className="text-xs text-zinc-400">
                Plataforma: {sistemaCfg.tiempo_cortesia_minutos} min cortesía · {sistemaCfg.pausa_cafe_minutos} min pausa reconocida
              </p>
            </div>
            <Switch
              checked={empresaCfg.usar_sistema_cortesia}
              onCheckedChange={(v) => setEmpresaCfg({ ...empresaCfg, usar_sistema_cortesia: v })}
            />
          </div>
          {!empresaCfg.usar_sistema_cortesia && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Tiempo de cortesía (min)</Label>
                <Input
                  type="number"
                  min={0}
                  max={120}
                  value={empresaCfg.tiempo_cortesia_minutos}
                  onChange={(e) => setEmpresaCfg({ ...empresaCfg, tiempo_cortesia_minutos: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Pausa reconocida / descanso (min)</Label>
                <Input
                  type="number"
                  min={0}
                  max={60}
                  value={empresaCfg.pausa_cafe_minutos}
                  onChange={(e) => setEmpresaCfg({ ...empresaCfg, pausa_cafe_minutos: e.target.value })}
                />
              </div>
            </div>
          )}
          <div className="rounded-lg border border-amber-100 bg-amber-50/50 px-4 py-3 text-xs text-amber-950/80 space-y-1.5">
            <p className="font-semibold text-amber-900">Pausas establecidas (cualquier motivo: café, fumar, personal…)</p>
            <ul className="list-disc space-y-1 pl-4">
              <li>
                <strong>Descanso mínimo legal de 15 min</strong> cuando la jornada diaria continuada supera las 6 horas.
                Solo cuenta como tiempo efectivo si lo reconoce el convenio o el contrato.
              </li>
              <li>
                La <strong>pausa reconocida</strong> configurada arriba ({empresaCfg.usar_sistema_cortesia ? sistemaCfg.pausa_cafe_minutos : empresaCfg.pausa_cafe_minutos} min)
                es el cupo diario que el sistema trata como tiempo efectivo; el resto de pausas (café, fumar, etc.) consume ese cupo.
              </li>
              <li>
                <strong>Pausa extra</strong> fuera de ese cupo: se ficha y el exceso se descuenta (o se puede exigir recuperar).
              </li>
              <li>
                Que se haya tolerado durante años no convierte automáticamente la pausa extra en un derecho adquirido.
              </li>
            </ul>
          </div>
          <div className="space-y-2 rounded-lg border border-orange-100 bg-orange-50/40 p-4">
            <Label>Umbral de horas extra (minutos)</Label>
            <p className="text-xs text-zinc-500">
              Si el trabajador supera el horario previsto en más de este umbral, podrá solicitar autorización de horas extra.
              Hasta que un encargado o admin la apruebe, ese tiempo <strong>no</strong> entra en el saldo compensable.
              El fichaje real siempre se conserva.
            </p>
            <Input
              type="number"
              min={0}
              max={240}
              value={empresaCfg.hora_extra_umbral_minutos ?? 15}
              onChange={(e) => setEmpresaCfg({ ...empresaCfg, hora_extra_umbral_minutos: e.target.value })}
              className="max-w-[8rem]"
            />
          </div>
          </AjustesSubbloque>

          <AjustesSubbloque
            id="jornada-vacaciones"
            title="Vacaciones y asuntos propios"
            subtitle="Modo de cómputo, días anuales y arrastre"
            icon={Palmtree}
            iconColor="text-emerald-600"
            borderClass="border-emerald-100 bg-emerald-50/30"
          >
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              Contabilización de vacaciones (empresa)
            </Label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setEmpresaCfg({
                  ...empresaCfg,
                  vacaciones_modo: "naturales",
                  dias_vacaciones: diasPorModoVacaciones("naturales"),
                })}
                className={`rounded-lg border px-4 py-3 text-left text-sm transition-colors ${
                  empresaCfg.vacaciones_modo === "naturales"
                    ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                    : "border-zinc-200 hover:bg-zinc-50"
                }`}
              >
                <strong>Días naturales</strong>
                <p className="mt-1 text-xs opacity-80">30 días por defecto · incluye fines de semana y festivos</p>
              </button>
              <button
                type="button"
                onClick={() => setEmpresaCfg({
                  ...empresaCfg,
                  vacaciones_modo: "habiles",
                  dias_vacaciones: diasPorModoVacaciones("habiles"),
                })}
                className={`rounded-lg border px-4 py-3 text-left text-sm transition-colors ${
                  empresaCfg.vacaciones_modo === "habiles"
                    ? "border-sky-300 bg-sky-50 text-sky-900"
                    : "border-zinc-200 hover:bg-zinc-50"
                }`}
              >
                <strong>Días hábiles</strong>
                <p className="mt-1 text-xs opacity-80">22 días por defecto · excluye fines de semana y festivos</p>
              </button>
            </div>
            <div className="space-y-1.5">
              <Label>Días de vacaciones anuales (empresa)</Label>
              <Input
                type="number"
                min={0}
                max={366}
                value={empresaCfg.dias_vacaciones}
                onChange={(e) => setEmpresaCfg({ ...empresaCfg, dias_vacaciones: e.target.value })}
                className="max-w-xs"
              />
              <p className="text-xs text-zinc-400">
                Puedes personalizar el total. Al cambiar el modo se sugiere {diasPorModoVacaciones(empresaCfg.vacaciones_modo)} días.
              </p>
            </div>
            <div className="space-y-3 rounded-lg border border-emerald-100 bg-emerald-50/40 p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">Arrastre entre años</p>
                  <p className="text-xs text-zinc-500">
                    Suma al cupo del año los días pendientes (o la deuda) del año anterior.
                    También se puede solicitar ya el periodo del año siguiente; esos días se descuentan del cupo de ese año.
                  </p>
                </div>
                <Switch
                  checked={!!empresaCfg.vacaciones_arrastre_activo}
                  onCheckedChange={(v) => setEmpresaCfg({ ...empresaCfg, vacaciones_arrastre_activo: v })}
                />
              </div>
              {empresaCfg.vacaciones_arrastre_activo && (
                <>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium">Permitir saldo negativo</p>
                      <p className="text-xs text-zinc-500">
                        Si se disfrutan más días de los asignados, el exceso se resta del año siguiente.
                      </p>
                    </div>
                    <Switch
                      checked={!!empresaCfg.vacaciones_arrastre_permite_negativo}
                      onCheckedChange={(v) => setEmpresaCfg({ ...empresaCfg, vacaciones_arrastre_permite_negativo: v })}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Tope máximo de días arrastrados (opcional)</Label>
                    <Input
                      type="number"
                      min={0}
                      max={365}
                      placeholder="Sin tope"
                      value={empresaCfg.vacaciones_arrastre_max}
                      onChange={(e) => setEmpresaCfg({ ...empresaCfg, vacaciones_arrastre_max: e.target.value })}
                      className="max-w-xs"
                    />
                    <p className="text-xs text-zinc-400">Vacío = sin límite al remanente positivo.</p>
                  </div>
                </>
              )}
            </div>
            <div className="space-y-3 border-t border-zinc-100 pt-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">Días de asuntos propios</p>
                  <p className="text-xs text-zinc-500">
                    Permisos retribuidos sin justificación (según convenio o acuerdo de empresa).
                  </p>
                </div>
                <Switch
                  checked={empresaCfg.asuntos_propios_activo}
                  onCheckedChange={(v) => setEmpresaCfg({ ...empresaCfg, asuntos_propios_activo: v })}
                  data-testid="ajustes-asuntos-propios-activo"
                />
              </div>
              {empresaCfg.asuntos_propios_activo && (
                <div className="space-y-1.5">
                  <Label>Días anuales por defecto (empresa)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={366}
                    value={empresaCfg.dias_asuntos_propios}
                    onChange={(e) => setEmpresaCfg({ ...empresaCfg, dias_asuntos_propios: e.target.value })}
                    className="max-w-xs"
                    data-testid="ajustes-asuntos-propios-input"
                  />
                  <p className="text-xs text-zinc-400">
                    Se cuentan como días laborables. Personalizable por trabajador en su ficha.
                  </p>
                </div>
              )}
            </div>
          </div>
          </AjustesSubbloque>

          <AjustesSubbloque
            id="jornada-festivos"
            title="Festivos en domingo"
            subtitle="Traslado al lunes siguiente (Estatuto de los Trabajadores)"
            icon={CalendarDays}
            iconColor="text-rose-600"
            borderClass="border-rose-100 bg-rose-50/40"
            headerRight={
              <Switch
                checked={!!empresaCfg.festivo_domingo_a_lunes}
                onCheckedChange={(v) => setEmpresaCfg({ ...empresaCfg, festivo_domingo_a_lunes: v })}
                data-testid="ajustes-festivo-domingo-lunes"
              />
            }
          >
            <p className="text-xs text-zinc-600">
              Si un festivo (nacional o local) cae en domingo, se registra el <strong>lunes siguiente</strong>.
              Por defecto está activo. En <strong>sábado</strong> no se traslada.
              Los turnos rotativos con «festivos laborables» siguen trabajando ese día aunque figure como festivo.
            </p>
            <p className="text-xs text-zinc-500">
              Al guardar con el traslado activo, los festivos ya cargados en domingo se pasan al lunes automáticamente.
            </p>
          </AjustesSubbloque>

          <AjustesSubbloque
            id="jornada-autocierre"
            title="Cierre automático de jornada"
            subtitle="Cierra la salida olvidada tras el fin de horario + margen"
            icon={Clock}
            iconColor="text-indigo-600"
            borderClass="border-indigo-100 bg-indigo-50/40"
            headerRight={
              <Switch
                checked={empresaCfg.autocierre_jornada_activo}
                onCheckedChange={(v) => setEmpresaCfg({ ...empresaCfg, autocierre_jornada_activo: v })}
              />
            }
          >
            <p className="text-xs text-indigo-800/70">
              Si un trabajador olvida picar la salida, el sistema cierra la jornada tras el fin de su horario
              más un margen configurable. Se notifica al trabajador; el encargado valida en Solicitudes.
            </p>
            {empresaCfg.autocierre_jornada_activo ? (
              <div className="space-y-1.5 max-w-xs">
                <Label>Minutos de gracia tras el fin del horario</Label>
                <Input
                  type="number"
                  min={0}
                  max={480}
                  value={empresaCfg.autocierre_jornada_minutos_gracia}
                  onChange={(e) => setEmpresaCfg({ ...empresaCfg, autocierre_jornada_minutos_gracia: e.target.value })}
                />
              </div>
            ) : (
              <p className="text-xs text-zinc-500">Activa el interruptor para configurar el margen.</p>
            )}
          </AjustesSubbloque>

          <AjustesSubbloque
            id="jornada-recordatorio"
            title="Recordatorio de fichaje"
            subtitle="Aviso si acumula fichajes manuales"
            icon={Bell}
            iconColor="text-amber-600"
            borderClass="border-amber-100 bg-amber-50/40"
            headerRight={
              <Switch
                checked={empresaCfg.recordatorio_manuales_activo}
                onCheckedChange={(v) => setEmpresaCfg({ ...empresaCfg, recordatorio_manuales_activo: v })}
              />
            }
          >
            {empresaCfg.recordatorio_manuales_activo ? (
              <>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label>Mín. fichajes manuales</Label>
                    <Input
                      type="number"
                      min={1}
                      max={50}
                      value={empresaCfg.recordatorio_manuales_umbral}
                      onChange={(e) => setEmpresaCfg({ ...empresaCfg, recordatorio_manuales_umbral: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>En los últimos (días)</Label>
                    <Input
                      type="number"
                      min={1}
                      max={90}
                      value={empresaCfg.recordatorio_manuales_dias}
                      onChange={(e) => setEmpresaCfg({ ...empresaCfg, recordatorio_manuales_dias: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>No repetir antes de (días)</Label>
                    <Input
                      type="number"
                      min={1}
                      max={90}
                      value={empresaCfg.recordatorio_manuales_cooldown_dias}
                      onChange={(e) => setEmpresaCfg({ ...empresaCfg, recordatorio_manuales_cooldown_dias: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Mensaje (usa {"{nombre}"} para personalizar)</Label>
                  <Textarea
                    rows={4}
                    value={empresaCfg.recordatorio_manuales_mensaje}
                    onChange={(e) => setEmpresaCfg({ ...empresaCfg, recordatorio_manuales_mensaje: e.target.value })}
                  />
                </div>
              </>
            ) : (
              <p className="text-xs text-zinc-500">Activa el interruptor para configurar umbrales y el mensaje.</p>
            )}
          </AjustesSubbloque>
          <AjustesSubbloque
            id="fichaje-whatsapp"
            title="Validar jornada por WhatsApp"
            subtitle="Entrada y salida vía enlace · plantillas Meta/Altiria"
            icon={Smartphone}
            iconColor="text-emerald-600"
            borderClass="border-emerald-100 bg-emerald-50/40"
            headerRight={
              <>
                <GuiaPlantillasFichajeWhatsApp />
                <Switch
                  checked={empresaCfg.fichaje_whatsapp_activo}
                  onCheckedChange={(v) => setEmpresaCfg({ ...empresaCfg, fichaje_whatsapp_activo: v })}
                />
              </>
            }
          >
            {empresaCfg.fichaje_whatsapp_activo ? (
              <div className="space-y-4">
                <p className="text-xs text-emerald-900/70">
                  Requiere WhatsApp activo (Meta o Altiria), teléfono y preferencia WhatsApp.
                  El aviso se envía tras la hora prevista del picado pendiente (entrada o salida).
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Minutos después de la hora prevista de picado (entrada o salida)</Label>
                    <Input
                      type="number"
                      min={0}
                      max={480}
                      value={empresaCfg.fichaje_whatsapp_minutos_despues}
                      onChange={(e) =>
                        setEmpresaCfg({ ...empresaCfg, fichaje_whatsapp_minutos_despues: e.target.value })
                      }
                    />
                    <p className="text-xs text-zinc-500">
                      Ej.: entrada 09:00 y 15 min → aviso desde 09:15 si no hay entrada.
                    </p>
                  </div>
                  <div className="flex items-center justify-between rounded-lg border border-emerald-100 bg-white/70 px-3 py-2">
                    <div>
                      <p className="text-sm font-medium">Solo si no hay picados</p>
                      <p className="text-xs text-zinc-500">No avisar si ya hay entrada y salida</p>
                    </div>
                    <Switch
                      checked={empresaCfg.fichaje_whatsapp_solo_sin_picados}
                      onCheckedChange={(v) =>
                        setEmpresaCfg({ ...empresaCfg, fichaje_whatsapp_solo_sin_picados: v })
                      }
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Modo de picado al registrar</Label>
                  <Select
                    value={empresaCfg.fichaje_whatsapp_modo_picado || "automatico"}
                    onValueChange={(v) =>
                      setEmpresaCfg({ ...empresaCfg, fichaje_whatsapp_modo_picado: v })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="automatico">
                        Automático — hora real al pulsar (no es manual)
                      </SelectItem>
                      <SelectItem value="manual">
                        Manual — hora prevista del horario (como hasta ahora)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500">
                    Por defecto automático. «Modificar hora» en la mini-web sigue siendo picado manual.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label>URL pública de la app</Label>
                  <Input
                    placeholder="https://controlhorario.abmscloud.com o http://localhost:8083"
                    value={empresaCfg.fichaje_whatsapp_base_url}
                    onChange={(e) =>
                      setEmpresaCfg({ ...empresaCfg, fichaje_whatsapp_base_url: e.target.value })
                    }
                  />
                  <p className="text-xs text-emerald-800/60">
                    Se usa en el enlace del WhatsApp. En producción puedes dejarla vacía si existe
                    la variable de entorno PUBLIC_APP_URL.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Plantilla Meta (opcional)</Label>
                    <Input
                      placeholder="nombre_plantilla_aprobada"
                      value={empresaCfg.fichaje_whatsapp_plantilla_meta}
                      onChange={(e) =>
                        setEmpresaCfg({ ...empresaCfg, fichaje_whatsapp_plantilla_meta: e.target.value })
                      }
                    />
                    <p className="text-xs text-zinc-500">
                      Variables sugeridas: nombre, fecha, horario, enlace. Si falla, se envía texto.
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Plantilla Altiria ID (opcional)</Label>
                    <Input
                      type="number"
                      placeholder="ID numérico"
                      value={empresaCfg.fichaje_whatsapp_plantilla_altiria_id}
                      onChange={(e) =>
                        setEmpresaCfg({
                          ...empresaCfg,
                          fichaje_whatsapp_plantilla_altiria_id: e.target.value,
                        })
                      }
                    />
                    <p className="text-xs text-zinc-500">
                      ID de la plantilla aprobada en Altiria (4 variables: nombre, fecha, horario, enlace).
                      Usa «Guía de plantillas» para copiar el texto.
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="border-emerald-200 text-emerald-800"
                  disabled={guardandoEmpresa}
                  onClick={async () => {
                    setGuardandoEmpresa(true);
                    try {
                      await api.put("/ajustes/empresa", {
                        usar_sistema_cortesia: empresaCfg.usar_sistema_cortesia,
                        tiempo_cortesia_minutos: empresaCfg.usar_sistema_cortesia
                          ? null
                          : Number(empresaCfg.tiempo_cortesia_minutos),
                        pausa_cafe_minutos: empresaCfg.usar_sistema_cortesia
                          ? null
                          : Number(empresaCfg.pausa_cafe_minutos),
                        vacaciones_modo: empresaCfg.vacaciones_modo,
                        dias_vacaciones: Number(empresaCfg.dias_vacaciones),
                        vacaciones_arrastre_activo: !!empresaCfg.vacaciones_arrastre_activo,
                        vacaciones_arrastre_max:
                          empresaCfg.vacaciones_arrastre_max === "" ||
                          empresaCfg.vacaciones_arrastre_max == null
                            ? null
                            : Number(empresaCfg.vacaciones_arrastre_max),
                        vacaciones_arrastre_permite_negativo:
                          !!empresaCfg.vacaciones_arrastre_permite_negativo,
                        dias_asuntos_propios: Number(empresaCfg.dias_asuntos_propios),
                        asuntos_propios_activo: empresaCfg.asuntos_propios_activo,
                        recordatorio_manuales_activo: empresaCfg.recordatorio_manuales_activo,
                        recordatorio_manuales_umbral: Number(empresaCfg.recordatorio_manuales_umbral),
                        recordatorio_manuales_dias: Number(empresaCfg.recordatorio_manuales_dias),
                        recordatorio_manuales_cooldown_dias: Number(
                          empresaCfg.recordatorio_manuales_cooldown_dias
                        ),
                        recordatorio_manuales_mensaje: empresaCfg.recordatorio_manuales_mensaje,
                        autocierre_jornada_activo: empresaCfg.autocierre_jornada_activo,
                        autocierre_jornada_minutos_gracia: Number(
                          empresaCfg.autocierre_jornada_minutos_gracia
                        ),
                        cumpleanos_activo: empresaCfg.cumpleanos_activo,
                        cumpleanos_mensaje: empresaCfg.cumpleanos_mensaje,
                        cumpleanos_equipo_mensaje: empresaCfg.cumpleanos_equipo_mensaje,
                        fichaje_whatsapp_activo: true,
                        fichaje_whatsapp_minutos_despues: Number(
                          empresaCfg.fichaje_whatsapp_minutos_despues
                        ),
                        fichaje_whatsapp_solo_sin_picados:
                          !!empresaCfg.fichaje_whatsapp_solo_sin_picados,
                        fichaje_whatsapp_plantilla_meta:
                          empresaCfg.fichaje_whatsapp_plantilla_meta || "",
                        fichaje_whatsapp_plantilla_altiria_id:
                          empresaCfg.fichaje_whatsapp_plantilla_altiria_id === "" ||
                          empresaCfg.fichaje_whatsapp_plantilla_altiria_id == null
                            ? null
                            : Number(empresaCfg.fichaje_whatsapp_plantilla_altiria_id),
                        fichaje_whatsapp_base_url: (empresaCfg.fichaje_whatsapp_base_url || "").trim(),
                        fichaje_whatsapp_modo_picado:
                          empresaCfg.fichaje_whatsapp_modo_picado === "manual"
                            ? "manual"
                            : "automatico",
                        hora_extra_umbral_minutos: Number(empresaCfg.hora_extra_umbral_minutos ?? 15),
                      });
                      const { data } = await api.post("/fichaje-whatsapp/admin/probar");
                      toast.success(data.mensaje || "Prueba enviada");
                      if (data.url) toast.message(`Enlace: ${data.url}`);
                      cargar();
                    } catch (e) {
                      toast.error(formatApiError(e));
                    } finally {
                      setGuardandoEmpresa(false);
                    }
                  }}
                >
                  <Send className="mr-2 h-4 w-4" /> Guardar y enviarme prueba
                </Button>
              </div>
            ) : (
              <p className="text-xs text-zinc-500">Activa el interruptor para ver plantillas, URL y prueba.</p>
            )}
          </AjustesSubbloque>

          <AjustesSubbloque
            id="fichaje-telegram"
            title="Validar jornada por Telegram"
            subtitle="Entrada y salida vía bot · sin plantillas Meta/Altiria"
            icon={Send}
            iconColor="text-sky-600"
            borderClass="border-sky-100 bg-sky-50/40"
            headerRight={
              <>
                <GuiaFichajeTelegram />
                <Switch
                  checked={empresaCfg.fichaje_telegram_activo}
                  onCheckedChange={(v) => setEmpresaCfg({ ...empresaCfg, fichaje_telegram_activo: v })}
                  data-testid="fichaje-telegram-activo-switch"
                />
              </>
            }
          >
            {empresaCfg.fichaje_telegram_activo ? (
              <div className="space-y-4">
                <p className="text-xs text-sky-900/70">
                  Requiere bot Telegram activo, trabajador vinculado (Empleados → Enviar vínculo) y la misma
                  URL pública que WhatsApp. El aviso se evalúa periódicamente (intervalo configurable en
                  Súper admin → Configuración) tras la hora prevista del picado pendiente.
                </p>
                <div className="space-y-1.5">
                  <Label>URL pública de la app (enlace del mensaje)</Label>
                  <Input
                    placeholder="https://controlhorario.abmscloud.com"
                    value={empresaCfg.fichaje_whatsapp_base_url}
                    onChange={(e) =>
                      setEmpresaCfg({ ...empresaCfg, fichaje_whatsapp_base_url: e.target.value })
                    }
                  />
                  <p className="text-xs text-zinc-500">
                    Sin esta URL (o PUBLIC_APP_URL) el bot no puede enviar los botones. Misma que en
                    Validar jornada por WhatsApp.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Minutos después de la hora prevista de picado (entrada o salida)</Label>
                    <Input
                      type="number"
                      min={0}
                      max={480}
                      value={empresaCfg.fichaje_telegram_minutos_despues}
                      onChange={(e) =>
                        setEmpresaCfg({
                          ...empresaCfg,
                          fichaje_telegram_minutos_despues: e.target.value,
                        })
                      }
                    />
                    <p className="text-xs text-zinc-500">
                      Ej.: entrada 08:50 y 5 min → aviso desde 08:55 si falta la entrada.
                    </p>
                  </div>
                  <div className="flex items-center justify-between rounded-lg border border-sky-100 bg-white/70 px-3 py-2">
                    <div>
                      <p className="text-sm font-medium">Solo si no hay picados</p>
                      <p className="text-xs text-zinc-500">No avisar si ya hay entrada y salida</p>
                    </div>
                    <Switch
                      checked={empresaCfg.fichaje_telegram_solo_sin_picados}
                      onCheckedChange={(v) =>
                        setEmpresaCfg({ ...empresaCfg, fichaje_telegram_solo_sin_picados: v })
                      }
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Modo de picado al registrar</Label>
                  <Select
                    value={empresaCfg.fichaje_telegram_modo_picado || "automatico"}
                    onValueChange={(v) =>
                      setEmpresaCfg({ ...empresaCfg, fichaje_telegram_modo_picado: v })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="automatico">
                        Automático — hora real al pulsar (no es manual)
                      </SelectItem>
                      <SelectItem value="manual">
                        Manual — hora prevista del horario (como hasta ahora)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500">
                    Por defecto automático. «Modificar hora» en la mini-web sigue siendo picado manual.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="border-sky-200 text-sky-800"
                  disabled={guardandoEmpresa}
                  onClick={async () => {
                    setGuardandoEmpresa(true);
                    try {
                      await api.put("/ajustes/empresa", {
                        usar_sistema_cortesia: empresaCfg.usar_sistema_cortesia,
                        tiempo_cortesia_minutos: empresaCfg.usar_sistema_cortesia
                          ? null
                          : Number(empresaCfg.tiempo_cortesia_minutos),
                        pausa_cafe_minutos: empresaCfg.usar_sistema_cortesia
                          ? null
                          : Number(empresaCfg.pausa_cafe_minutos),
                        vacaciones_modo: empresaCfg.vacaciones_modo,
                        dias_vacaciones: Number(empresaCfg.dias_vacaciones),
                        vacaciones_arrastre_activo: !!empresaCfg.vacaciones_arrastre_activo,
                        vacaciones_arrastre_max:
                          empresaCfg.vacaciones_arrastre_max === "" ||
                          empresaCfg.vacaciones_arrastre_max == null
                            ? null
                            : Number(empresaCfg.vacaciones_arrastre_max),
                        vacaciones_arrastre_permite_negativo:
                          !!empresaCfg.vacaciones_arrastre_permite_negativo,
                        dias_asuntos_propios: Number(empresaCfg.dias_asuntos_propios),
                        asuntos_propios_activo: empresaCfg.asuntos_propios_activo,
                        recordatorio_manuales_activo: empresaCfg.recordatorio_manuales_activo,
                        recordatorio_manuales_umbral: Number(empresaCfg.recordatorio_manuales_umbral),
                        recordatorio_manuales_dias: Number(empresaCfg.recordatorio_manuales_dias),
                        recordatorio_manuales_cooldown_dias: Number(
                          empresaCfg.recordatorio_manuales_cooldown_dias
                        ),
                        recordatorio_manuales_mensaje: empresaCfg.recordatorio_manuales_mensaje,
                        autocierre_jornada_activo: empresaCfg.autocierre_jornada_activo,
                        autocierre_jornada_minutos_gracia: Number(
                          empresaCfg.autocierre_jornada_minutos_gracia
                        ),
                        cumpleanos_activo: empresaCfg.cumpleanos_activo,
                        cumpleanos_mensaje: empresaCfg.cumpleanos_mensaje,
                        cumpleanos_equipo_mensaje: empresaCfg.cumpleanos_equipo_mensaje,
                        fichaje_whatsapp_activo: !!empresaCfg.fichaje_whatsapp_activo,
                        fichaje_whatsapp_minutos_despues: Number(
                          empresaCfg.fichaje_whatsapp_minutos_despues
                        ),
                        fichaje_whatsapp_solo_sin_picados:
                          !!empresaCfg.fichaje_whatsapp_solo_sin_picados,
                        fichaje_whatsapp_plantilla_meta:
                          empresaCfg.fichaje_whatsapp_plantilla_meta || "",
                        fichaje_whatsapp_plantilla_altiria_id:
                          empresaCfg.fichaje_whatsapp_plantilla_altiria_id === "" ||
                          empresaCfg.fichaje_whatsapp_plantilla_altiria_id == null
                            ? null
                            : Number(empresaCfg.fichaje_whatsapp_plantilla_altiria_id),
                        fichaje_whatsapp_base_url: (empresaCfg.fichaje_whatsapp_base_url || "").trim() || "http://localhost:8083",
                        fichaje_whatsapp_modo_picado:
                          empresaCfg.fichaje_whatsapp_modo_picado === "manual"
                            ? "manual"
                            : "automatico",
                        fichaje_telegram_activo: true,
                        fichaje_telegram_minutos_despues: Number(
                          empresaCfg.fichaje_telegram_minutos_despues
                        ),
                        fichaje_telegram_solo_sin_picados:
                          !!empresaCfg.fichaje_telegram_solo_sin_picados,
                        fichaje_telegram_modo_picado:
                          empresaCfg.fichaje_telegram_modo_picado === "manual"
                            ? "manual"
                            : "automatico",
                        hora_extra_umbral_minutos: Number(empresaCfg.hora_extra_umbral_minutos ?? 15),
                      });
                      const { data } = await api.post("/fichaje-whatsapp/admin/probar-telegram");
                      toast.success(data.mensaje || "Prueba enviada");
                      if (data.url) toast.message(`Enlace: ${data.url}`);
                      cargar();
                    } catch (e) {
                      toast.error(formatApiError(e));
                    } finally {
                      setGuardandoEmpresa(false);
                    }
                  }}
                >
                  <Send className="mr-2 h-4 w-4" /> Guardar y enviarme prueba
                </Button>
              </div>
            ) : (
              <p className="text-xs text-zinc-500">Activa el interruptor para configurar minutos y prueba.</p>
            )}
          </AjustesSubbloque>

          <AjustesSubbloque
            id="jornada-cumpleanos"
            title="Felicitación de cumpleaños"
            subtitle="Mensaje al cumpleañero y avisos al centro"
            icon={Cake}
            iconColor="text-pink-600"
            borderClass="border-pink-100 bg-pink-50/40"
            headerRight={
              <Switch
                checked={empresaCfg.cumpleanos_activo}
                onCheckedChange={(v) => setEmpresaCfg({ ...empresaCfg, cumpleanos_activo: v })}
              />
            }
          >
            {empresaCfg.cumpleanos_activo ? (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Mensaje al cumpleañero</Label>
                  <Textarea
                    placeholder="¡Feliz cumpleaños, {nombre}! Todo el equipo te desea un excelente día."
                    rows={3}
                    value={empresaCfg.cumpleanos_mensaje}
                    onChange={(e) => setEmpresaCfg({ ...empresaCfg, cumpleanos_mensaje: e.target.value })}
                  />
                  <p className="text-xs text-pink-800/60">
                    Se muestra como aviso emergente tras el primer picado del día y en Mis avisos.
                    Usa <code className="rounded bg-pink-100 px-1">{"{nombre}"}</code>.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label>Mensaje a compañeros del centro</Label>
                  <Textarea
                    placeholder="Hoy {nombre} cumple años en {centro}. ¡Felicítale y que tenga un gran día!"
                    rows={3}
                    value={empresaCfg.cumpleanos_equipo_mensaje}
                    onChange={(e) => setEmpresaCfg({ ...empresaCfg, cumpleanos_equipo_mensaje: e.target.value })}
                  />
                  <p className="text-xs text-pink-800/60">
                    Variables: <code className="rounded bg-pink-100 px-1">{"{nombre}"}</code>,{" "}
                    <code className="rounded bg-pink-100 px-1">{"{centro}"}</code>.
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-xs text-zinc-500">Activa el interruptor para editar los mensajes.</p>
            )}
          </AjustesSubbloque>

          <Button onClick={guardarEmpresa} disabled={guardandoEmpresa} className="rounded-lg bg-violet-600 hover:bg-violet-700">
            {guardandoEmpresa ? "Guardando…" : "Guardar jornada y vacaciones"}
          </Button>
        </div>
      </AjustesSeccion>
      </div>

      <AjustesSeccion
        id="gastos-config"
        title="Gastos comerciales"
        subtitle="Kilometraje, desviaciones de ruta y validación GPS de dietas"
        icon={Receipt}
        iconClass="bg-gradient-to-br from-emerald-500 to-teal-600"
        borderClass="border-emerald-100"
      >
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Tarifa € / km</Label>
              <Input
                type="text"
                inputMode="decimal"
                value={empresaCfg.gastos_tarifa_km}
                onChange={(e) => setEmpresaCfg({ ...empresaCfg, gastos_tarifa_km: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Margen desviación (%)</Label>
              <Input
                type="text"
                inputMode="decimal"
                value={empresaCfg.gastos_margen_desviacion_km}
                onChange={(e) => setEmpresaCfg({ ...empresaCfg, gastos_margen_desviacion_km: e.target.value })}
              />
              <p className="text-[11px] text-zinc-500">
                Si |km indicados − km de ruta| supera este % sobre los km de ruta, el listado lo marca como no cuadrado.
                Ej.: 10 % sobre 100 km de ruta permite hasta 10 km de diferencia.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Radio GPS dietas (metros)</Label>
              <Input
                type="number"
                min={50}
                max={5000}
                value={empresaCfg.gastos_radio_dieta_metros}
                onChange={(e) => setEmpresaCfg({ ...empresaCfg, gastos_radio_dieta_metros: e.target.value })}
              />
            </div>
          </div>
          <Button onClick={guardarGastos} disabled={guardandoGastos} className="rounded-lg bg-emerald-600 hover:bg-emerald-700">
            {guardandoGastos ? "Guardando…" : "Guardar gastos comerciales"}
          </Button>
      </AjustesSeccion>

      <AjustesSeccion
        id="correo-smtp"
        title="Servidor de correo (SMTP)"
        subtitle="Como en Outlook: servidor, puerto, usuario y contraseña"
        icon={Mail}
        iconClass="bg-gradient-to-br from-indigo-500 to-violet-600"
        borderClass="border-indigo-100"
        headerRight={
          <Badge
            variant="outline"
            className={form.activo ? "border-green-200 bg-green-100 text-green-700" : "border-zinc-200 bg-zinc-100 text-zinc-500"}
            data-testid="correo-estado-badge"
          >
            {form.activo ? "Activo" : "Inactivo"}
          </Badge>
        }
      >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_7rem]">
            <div className="space-y-1.5">
              <Label>Servidor SMTP</Label>
              <Input
                placeholder="smtp.office365.com"
                value={form.smtp_host}
                onChange={(e) => setForm({ ...form, smtp_host: e.target.value })}
                data-testid="smtp-host-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Puerto</Label>
              <Input
                type="number"
                value={form.smtp_port}
                onChange={(e) => setForm({ ...form, smtp_port: e.target.value })}
                data-testid="smtp-port-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Usuario</Label>
              <Input
                placeholder="notificaciones@tuempresa.es"
                value={form.smtp_user}
                onChange={(e) => setForm({ ...form, smtp_user: e.target.value })}
                data-testid="smtp-user-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Contraseña</Label>
              <Input
                type="password"
                placeholder={passwordGuardada ? "•••••••• (guardada, dejar vacío para mantener)" : "Contraseña SMTP"}
                value={form.smtp_password}
                onChange={(e) => setForm({ ...form, smtp_password: e.target.value })}
                data-testid="smtp-password-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Email del remitente</Label>
              <Input
                placeholder="notificaciones@tuempresa.es"
                value={form.sender_email}
                onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
                data-testid="sender-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Nombre del remitente</Label>
              <Input
                value={form.sender_name}
                onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
                data-testid="sender-name-input"
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-zinc-100 px-4 py-3">
            <div>
              <p className="text-sm font-medium">Seguridad TLS (STARTTLS)</p>
              <p className="text-xs text-zinc-400">Recomendado con puerto 587 · el puerto 465 usa SSL automáticamente</p>
            </div>
            <Switch
              checked={form.use_tls}
              onCheckedChange={(v) => setForm({ ...form, use_tls: v })}
              data-testid="smtp-tls-switch"
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border border-zinc-100 px-4 py-3">
            <div>
              <p className="text-sm font-medium">Correo SMTP activo</p>
              <p className="text-xs text-zinc-400">
                Activa o desactiva todos los envíos por email de la empresa (solicitudes, festivos, comunicados, ficha…).
                El detalle por tipo de aviso se configura en «Canales por evento».
              </p>
            </div>
            <Switch
              checked={form.activo}
              onCheckedChange={(v) => setForm({ ...form, activo: v })}
              data-testid="correo-activo-switch"
            />
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Button onClick={guardar} disabled={guardando} data-testid="guardar-correo-btn" className="rounded-lg bg-indigo-600 hover:bg-indigo-700">
              {guardando ? "Guardando..." : "Guardar configuración"}
            </Button>
            <Button variant="outline" onClick={probar} disabled={probando} data-testid="probar-correo-btn" className="gap-1.5 rounded-lg">
              <Send className="h-4 w-4" strokeWidth={1.5} />
              {probando ? "Enviando..." : "Enviar email de prueba"}
            </Button>
          </div>

          <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-5 text-sm text-zinc-600">
            <p className="font-medium text-zinc-950">Ejemplos de configuración habituales</p>
            <ul className="mt-2 space-y-1 text-xs">
              <li>• <b>Outlook / Microsoft 365:</b> smtp.office365.com · puerto 587 · TLS activado · tu email y contraseña</li>
              <li>• <b>Gmail / Google Workspace:</b> smtp.gmail.com · puerto 587 · TLS activado · requiere "contraseña de aplicación"</li>
              <li>• <b>Resend:</b> smtp.resend.com · puerto 587 · usuario "resend" · contraseña = API key</li>
              <li>• <b>Hosting propio (cPanel, IONOS, etc.):</b> mail.tudominio.es · puerto 465 o 587</li>
            </ul>
            <p className="mt-3 text-xs text-zinc-400">
              🔒 La contraseña se guarda únicamente en el servidor y nunca se vuelve a mostrar ni se envía al navegador.
            </p>
          </div>
      </AjustesSeccion>

      <AjustesSeccion
        id="whatsapp-config"
        title="WhatsApp Cloud API (Meta)"
        subtitle="API oficial de WhatsApp Business · Graph API"
        icon={MessageCircle}
        iconClass="bg-gradient-to-br from-emerald-500 to-teal-600"
        borderClass="border-emerald-100"
        headerRight={
          <Badge
            variant="outline"
            className={
              waForm.activo && waEstado.status === "connected"
                ? "border-green-200 bg-green-100 text-green-700"
                : "border-zinc-200 bg-zinc-100 text-zinc-500"
            }
            data-testid="whatsapp-estado-badge"
          >
            {!waForm.activo
              ? "Inactivo"
              : waEstado.status === "connected"
                ? `Conectado${waEstado.phone_display ? ` · ${waEstado.phone_display}` : ""}`
                : waEstado.status || "Pendiente"}
          </Badge>
        }
      >
          <div className="rounded-lg border border-blue-100 bg-blue-50/80 px-4 py-3 text-xs text-blue-900">
            Necesitas una cuenta de <strong>Meta Business</strong>, una app en{" "}
            <a href="https://developers.facebook.com/" target="_blank" rel="noreferrer" className="underline">
              developers.facebook.com
            </a>
            , un número en WhatsApp Business Platform y un token de acceso permanente con permiso{" "}
            <code className="rounded bg-white/80 px-1">whatsapp_business_messaging</code>.
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Phone Number ID</Label>
              <Input
                placeholder="123456789012345"
                value={waForm.phone_number_id}
                onChange={(e) => setWaForm({ ...waForm, phone_number_id: e.target.value })}
                data-testid="whatsapp-phone-number-id"
              />
            </div>
            <div className="space-y-1.5">
              <Label>WABA ID (opcional)</Label>
              <Input
                placeholder="WhatsApp Business Account ID"
                value={waForm.waba_id}
                onChange={(e) => setWaForm({ ...waForm, waba_id: e.target.value })}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>
                Access Token {waTokenGuardado && <span className="text-zinc-400">(guardado)</span>}
              </Label>
              <Input
                type="password"
                placeholder={waTokenGuardado ? "••••••••" : "Token permanente de sistema"}
                value={waForm.access_token}
                onChange={(e) => setWaForm({ ...waForm, access_token: e.target.value })}
                data-testid="whatsapp-access-token"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Versión Graph API</Label>
              <Input
                placeholder="v21.0"
                value={waForm.graph_api_version}
                onChange={(e) => setWaForm({ ...waForm, graph_api_version: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>
                Webhook verify token {waVerifyGuardado && <span className="text-zinc-400">(guardado)</span>}
              </Label>
              <Input
                type="password"
                placeholder={waVerifyGuardado ? "••••••••" : "Token de verificación del webhook"}
                value={waForm.webhook_verify_token}
                onChange={(e) => setWaForm({ ...waForm, webhook_verify_token: e.target.value })}
              />
            </div>
          </div>

          <div className="rounded-lg border border-zinc-100 bg-zinc-50/80 px-4 py-3 text-xs text-zinc-600">
            <p className="font-medium text-zinc-800">URL del webhook (regístrala en Meta)</p>
            <p className="mt-1 break-all font-mono">{waForm.webhook_url || "/api/webhooks/whatsapp"}</p>
            <p className="mt-2">
              Los mensajes de texto libre requieren ventana de 24 h o plantillas aprobadas para avisos proactivos.
              Respeta las preferencias de cada trabajador.
            </p>
          </div>

          {waEstado.message && waForm.activo && waEstado.status !== "connected" && (
            <div className="rounded-lg border border-amber-100 bg-amber-50/80 px-4 py-3 text-xs text-amber-900">
              {waEstado.message}
            </div>
          )}

          <div className="flex items-center justify-between rounded-lg border border-zinc-100 px-4 py-3">
            <div>
              <p className="text-sm font-medium">WhatsApp activo en la empresa</p>
              <p className="text-xs text-zinc-400">Envía mensajes a trabajadores con la preferencia activada</p>
            </div>
            <Switch
              checked={waForm.activo}
              onCheckedChange={(v) => setWaForm({ ...waForm, activo: v })}
              data-testid="whatsapp-activo-switch"
            />
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Button onClick={guardarWhatsApp} disabled={guardandoWa} className="rounded-lg bg-emerald-600 hover:bg-emerald-700">
              {guardandoWa ? "Guardando…" : "Guardar WhatsApp"}
            </Button>
            <Button variant="outline" onClick={refrescarEstadoWa} className="gap-1.5 rounded-lg">
              <RefreshCw className="h-4 w-4" />
              Comprobar conexión
            </Button>
            <Button variant="outline" onClick={probarWhatsApp} disabled={probandoWa} className="gap-1.5 rounded-lg">
              <Send className="h-4 w-4" />
              {probandoWa ? "Enviando…" : "Enviar prueba"}
            </Button>
          </div>
      </AjustesSeccion>

      <AjustesSeccion
        id="telegram-config"
        title="Telegram Bot API"
        subtitle="Notificaciones por bot · BotFather"
        icon={Send}
        iconClass="bg-gradient-to-br from-sky-400 to-blue-600"
        borderClass="border-sky-100"
        headerRight={
          <div className="flex items-center gap-2">
            <GuiaTelegram />
            <Badge
              variant="outline"
              className={
                tgForm.activo && tgEstado.status === "connected"
                  ? "border-sky-200 bg-sky-100 text-sky-700"
                  : "border-zinc-200 bg-zinc-100 text-zinc-500"
              }
              data-testid="telegram-estado-badge"
            >
              {!tgForm.activo
                ? "Inactivo"
                : tgEstado.status === "connected"
                  ? `Conectado${tgEstado.bot_username ? ` · @${tgEstado.bot_username}` : ""}`
                  : tgEstado.status || "Pendiente"}
            </Badge>
          </div>
        }
      >
          <div className="rounded-lg border border-sky-100 bg-sky-50/80 px-4 py-3 text-xs text-sky-900">
            Crea un bot con <strong>@BotFather</strong>, guarda el token aquí y registra el webhook.
            Cada trabajador se vincula con un email que abre Telegram (<code className="rounded bg-white/80 px-1">t.me/…?start=…</code>).
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>
                Token del bot {tgTokenGuardado && <span className="text-zinc-400">(guardado)</span>}
              </Label>
              <Input
                type="password"
                placeholder={tgTokenGuardado ? "••••••••" : "123456:ABC-DEF…"}
                value={tgForm.bot_token}
                onChange={(e) => setTgForm({ ...tgForm, bot_token: e.target.value })}
                data-testid="telegram-bot-token"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Username del bot</Label>
              <Input
                placeholder="MiEmpresaBot"
                value={tgForm.bot_username}
                onChange={(e) => setTgForm({ ...tgForm, bot_username: e.target.value })}
                data-testid="telegram-bot-username"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Secret del webhook (opcional)</Label>
              <Input
                type="password"
                placeholder="Vacío = mantener · «nuevo» regenera"
                value={tgForm.webhook_secret}
                onChange={(e) => setTgForm({ ...tgForm, webhook_secret: e.target.value })}
              />
            </div>
          </div>

          <div className="rounded-lg border border-zinc-100 bg-zinc-50/80 px-4 py-3 text-xs text-zinc-600">
            <p className="font-medium text-zinc-800">URL del webhook</p>
            <p className="mt-1 break-all font-mono">{tgForm.webhook_url || "/api/webhooks/telegram"}</p>
            <p className="mt-2">
              Telegram exige HTTPS público. En local usa un túnel o configura el webhook solo en producción
              (<code className="mx-1 rounded bg-white px-1">PUBLIC_API_URL</code>).
            </p>
          </div>

          {tgEstado.message && tgForm.activo && tgEstado.status !== "connected" && (
            <div className="rounded-lg border border-amber-100 bg-amber-50/80 px-4 py-3 text-xs text-amber-900">
              {tgEstado.message}
            </div>
          )}

          <div className="flex items-center justify-between rounded-lg border border-zinc-100 px-4 py-3">
            <div>
              <p className="text-sm font-medium">Telegram activo en la empresa</p>
              <p className="text-xs text-zinc-400">Envía avisos a trabajadores vinculados</p>
            </div>
            <Switch
              checked={tgForm.activo}
              onCheckedChange={(v) => setTgForm({ ...tgForm, activo: v })}
              data-testid="telegram-activo-switch"
            />
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Button onClick={guardarTelegram} disabled={guardandoTg} className="rounded-lg bg-sky-600 hover:bg-sky-700">
              {guardandoTg ? "Guardando…" : "Guardar Telegram"}
            </Button>
            <Button variant="outline" onClick={refrescarEstadoTg} className="gap-1.5 rounded-lg">
              <RefreshCw className="h-4 w-4" />
              Comprobar bot
            </Button>
            <Button
              variant="outline"
              onClick={registrarWebhookTg}
              disabled={registrandoWebhookTg}
              className="gap-1.5 rounded-lg"
            >
              {registrandoWebhookTg ? "Registrando…" : "Registrar webhook"}
            </Button>
            <Button variant="outline" onClick={probarTelegram} disabled={probandoTg} className="gap-1.5 rounded-lg">
              <Send className="h-4 w-4" />
              {probandoTg ? "Enviando…" : "Enviar prueba"}
            </Button>
          </div>
      </AjustesSeccion>

      <AjustesSeccion
        id="altiria-config"
        title="Altiria (SMS y WhatsApp)"
        subtitle="Proveedor alternativo de SMS y WhatsApp Business"
        icon={Smartphone}
        iconClass="bg-gradient-to-br from-sky-500 to-blue-600"
        borderClass="border-sky-100"
        headerRight={
          <Badge
            variant="outline"
            className={
              altiriaForm.activo_sms || altiriaForm.activo_whatsapp
                ? "border-sky-200 bg-sky-100 text-sky-700"
                : "border-zinc-200 bg-zinc-100 text-zinc-500"
            }
          >
            {altiriaForm.activo_sms || altiriaForm.activo_whatsapp ? "Activo" : "Inactivo"}
          </Badge>
        }
      >
          <p className="text-sm text-zinc-600">
            Credenciales de la API REST actual de Altiria (<code className="text-[11px]">api.altiria.com</code>).
            SMS usa multiparte automático y <code className="text-[11px]">utf-16</code> si hay acentos.
            WhatsApp: conversación 24&nbsp;h y, si falla, plantilla de respaldo. Si Meta Cloud está activo, Meta tiene prioridad.
          </p>
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-emerald-100 bg-emerald-50/50 px-3 py-2.5">
            <p className="flex-1 text-xs text-emerald-900/80">
              Para el servicio <strong>Validar jornada por WhatsApp</strong> puedes usar una plantilla
              específica (inicio/fin de jornada). Cópiala desde la guía y pega el ID en ese bloque.
            </p>
            <GuiaPlantillasFichajeWhatsApp />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Usuario API</Label>
              <Input value={altiriaForm.api_usuario} onChange={(e) => setAltiriaForm({ ...altiriaForm, api_usuario: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Contraseña API {altiriaPasswordGuardada && <span className="text-xs text-zinc-400">(guardada)</span>}</Label>
              <Input
                type="password"
                value={altiriaForm.api_password}
                onChange={(e) => setAltiriaForm({ ...altiriaForm, api_password: e.target.value })}
                placeholder={altiriaPasswordGuardada ? "Dejar vacío para mantener" : ""}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Host API (opcional)</Label>
              <Input value={altiriaForm.api_host} onChange={(e) => setAltiriaForm({ ...altiriaForm, api_host: e.target.value })} placeholder="https://api.altiria.com" />
            </div>
            <div className="space-y-1.5">
              <Label>Remitente SMS</Label>
              <Input value={altiriaForm.remitente_sms} onChange={(e) => setAltiriaForm({ ...altiriaForm, remitente_sms: e.target.value })} placeholder="Nombre o número" />
            </div>
            <div className="space-y-1.5">
              <Label>Remitente WhatsApp</Label>
              <Input value={altiriaForm.remitente_whatsapp} onChange={(e) => setAltiriaForm({ ...altiriaForm, remitente_whatsapp: e.target.value })} placeholder="34XXXXXXXXX" />
            </div>
            <div className="space-y-1.5">
              <Label>ID plantilla WhatsApp (opcional)</Label>
              <Input
                type="number"
                value={altiriaForm.plantilla_whatsapp_id}
                onChange={(e) => setAltiriaForm({ ...altiriaForm, plantilla_whatsapp_id: e.target.value })}
                placeholder="Para mensajes fuera de ventana 24h"
              />
            </div>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-lg border border-sky-100 bg-sky-50/50 p-4">
            <div className="flex items-center justify-between gap-3 sm:flex-1">
              <div>
                <p className="text-sm font-medium">SMS activo</p>
                <p className="text-xs text-zinc-500">Envío de SMS de pago vía Altiria</p>
              </div>
              <Switch checked={altiriaForm.activo_sms} onCheckedChange={(v) => setAltiriaForm({ ...altiriaForm, activo_sms: v })} />
            </div>
            <div className="flex items-center justify-between gap-3 sm:flex-1">
              <div>
                <p className="text-sm font-medium">WhatsApp Altiria activo</p>
                <p className="text-xs text-zinc-500">Respaldo si Meta no está configurado</p>
              </div>
              <Switch checked={altiriaForm.activo_whatsapp} onCheckedChange={(v) => setAltiriaForm({ ...altiriaForm, activo_whatsapp: v })} />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={guardarAltiria} disabled={guardandoAltiria} className="rounded-lg bg-sky-600 hover:bg-sky-700">
              {guardandoAltiria ? "Guardando…" : "Guardar Altiria"}
            </Button>
            <Button variant="outline" onClick={verificarCuentaAltiria} disabled={verificandoAltiria} className="gap-1.5 rounded-lg">
              {verificandoAltiria ? "Comprobando…" : "Verificar cuenta"}
            </Button>
            <Button variant="outline" onClick={probarAltiriaSms} disabled={probandoAltiriaSms} className="gap-1.5 rounded-lg">
              <Send className="h-4 w-4" />
              {probandoAltiriaSms ? "Enviando…" : "Probar SMS"}
            </Button>
            <Button variant="outline" onClick={probarAltiriaWhatsapp} disabled={probandoAltiriaWa} className="gap-1.5 rounded-lg">
              <MessageCircle className="h-4 w-4" />
              {probandoAltiriaWa ? "Enviando…" : "Probar WhatsApp"}
            </Button>
          </div>
      </AjustesSeccion>

      <AjustesSeccion
        id="notificaciones-canales"
        title="Canales por tipo de notificación"
        subtitle="Email, SMS, WhatsApp y Telegram por evento"
        icon={Bell}
        iconClass="bg-gradient-to-br from-amber-500 to-orange-600"
        borderClass="border-amber-100"
      >
          <p className="text-sm text-zinc-600">
            La campana in-app siempre se muestra. Aquí eliges qué eventos también envían email, SMS, WhatsApp o Telegram.
            Tras cambiar un check, pulsa <b>Guardar preferencias de canales</b>; si no se guarda, el sistema sigue con el valor anterior
            (por defecto el email de «Cambio de días festivos» está activado).
          </p>
          <div className="overflow-x-auto rounded-lg border border-zinc-200">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-zinc-50 text-left text-xs text-zinc-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Evento</th>
                  <th className="px-3 py-2 font-medium text-center w-20">Email</th>
                  <th className="px-3 py-2 font-medium text-center w-20">SMS</th>
                  <th className="px-3 py-2 font-medium text-center w-24">WhatsApp</th>
                  <th className="px-3 py-2 font-medium text-center w-24">Telegram</th>
                </tr>
              </thead>
              <tbody>
                {notifCatalogo.map((ev) => (
                  <tr key={ev.id} className="border-t border-zinc-100">
                    <td className="px-3 py-2">
                      <span className="text-xs text-zinc-400">{ev.grupo}</span>
                      <p>{ev.label}</p>
                    </td>
                    {["email", "sms", "whatsapp", "telegram"].map((canal) => (
                      <td key={canal} className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-zinc-300"
                          checked={!!notifEventos[ev.id]?.[canal]}
                          onChange={() => toggleNotifCanal(ev.id, canal)}
                          data-testid={`notif-${ev.id}-${canal}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button onClick={guardarNotificacionesCanales} disabled={guardandoNotif} className="rounded-lg bg-amber-600 hover:bg-amber-700">
            {guardandoNotif ? "Guardando…" : "Guardar preferencias de canales"}
          </Button>
      </AjustesSeccion>

      <AjustesSeccion
        id="solicitudes-aprobacion"
        title="Aprobación de solicitudes"
        subtitle="Quién recibe el aviso y quién puede aprobar cada tipo"
        icon={ClipboardCheck}
        iconClass="bg-gradient-to-br from-emerald-500 to-teal-600"
        borderClass="border-emerald-100"
      >
          <p className="text-sm text-zinc-600">
            Marca <b>Administrador</b>, <b>Encargado</b> o ambos en cada columna. Si un tipo solo lo aprueba el encargado y un
            trabajador no tiene encargado en su departamento, el administrador podrá resolverlo como respaldo.
            La columna <b>Grupo Gerencia</b> es el default al dar de alta un miembro en ese grupo; lo marcado
            lo gestiona el aprobador de Gerencia y el encargado del departamento deja de recibirlo.
            {gerenciaModulo ? (
              <> Configura el grupo en <a href="/admin/grupo-gerencia" className="text-emerald-700 underline">Grupo Gerencia</a>.</>
            ) : (
              <> El módulo está desactivado; actívalo en la plataforma (Empresas) para usarlo.</>
            )}
          </p>
          <div className="overflow-x-auto rounded-lg border border-zinc-200">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-zinc-50 text-left text-xs text-zinc-500">
                <tr>
                  <th className="px-3 py-2 font-medium" rowSpan={2}>Tipo</th>
                  <th className="px-3 py-2 font-medium text-center border-l border-zinc-200" colSpan={2}>Recibe la notificación</th>
                  <th className="px-3 py-2 font-medium text-center border-l border-zinc-200" colSpan={2}>Puede aprobar / rechazar</th>
                  <th className="px-3 py-2 font-medium text-center border-l border-zinc-200 w-28" rowSpan={2}>
                    Grupo Gerencia
                  </th>
                </tr>
                <tr>
                  <th className="px-3 py-2 font-medium text-center border-l border-zinc-200 w-24">Admin.</th>
                  <th className="px-3 py-2 font-medium text-center w-24">Encargado</th>
                  <th className="px-3 py-2 font-medium text-center border-l border-zinc-200 w-24">Admin.</th>
                  <th className="px-3 py-2 font-medium text-center w-24">Encargado</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const secciones = [
                    { id: "solicitudes", titulo: "Solicitudes del trabajador" },
                    { id: "modulos", titulo: "Avisos y revisiones de módulos" },
                  ];
                  const filas = [];
                  for (const sec of secciones) {
                    const grupos = aprobCatalogo.filter((g) => (g.seccion || "solicitudes") === sec.id);
                    if (!grupos.length) continue;
                    filas.push(
                      <tr key={`sec-${sec.id}`} className="border-t border-zinc-200 bg-zinc-50/80">
                        <td colSpan={6} className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">
                          {sec.titulo}
                        </td>
                      </tr>
                    );
                    for (const g of grupos) {
                      const cfg = aprobConfig[g.id] || { aprobadores: [], notificar: [], gerencia: false };
                      filas.push(
                        <tr key={g.id} className="border-t border-zinc-100">
                          <td className="px-3 py-2">{g.label}</td>
                          {[
                            { campo: "notificar", rol: "admin" },
                            { campo: "notificar", rol: "manager" },
                            { campo: "aprobadores", rol: "admin" },
                            { campo: "aprobadores", rol: "manager" },
                          ].map(({ campo, rol }, i) => (
                            <td
                              key={`${campo}-${rol}`}
                              className={`px-3 py-2 text-center ${i === 0 || i === 2 ? "border-l border-zinc-200" : ""}`}
                            >
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-zinc-300"
                                checked={(cfg[campo] || []).includes(rol)}
                                onChange={() => toggleAprobRol(g.id, campo, rol)}
                                data-testid={`aprob-${g.id}-${campo}-${rol}`}
                              />
                            </td>
                          ))}
                          <td className="px-3 py-2 text-center border-l border-zinc-200">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-zinc-300"
                              checked={!!cfg.gerencia}
                              onChange={() => toggleAprobGerencia(g.id)}
                              data-testid={`aprob-${g.id}-gerencia`}
                            />
                          </td>
                        </tr>
                      );
                    }
                  }
                  return filas;
                })()}
              </tbody>
            </table>
          </div>
          <Button onClick={guardarSolicitudesAprobacion} disabled={guardandoAprob} className="rounded-lg bg-emerald-600 hover:bg-emerald-700">
            {guardandoAprob ? "Guardando…" : "Guardar aprobaciones"}
          </Button>
      </AjustesSeccion>

      <AjustesSeccion
        id="quiosco-config"
        title="Modo quiosco"
        subtitle="Tablet en la entrada · fichaje con PIN"
        icon={Tablet}
        iconClass="bg-gradient-to-br from-violet-500 to-purple-600"
        borderClass="border-indigo-100"
        headerRight={
          <Badge
            variant="outline"
            className={kioskToken ? "border-green-200 bg-green-100 text-green-700" : "border-zinc-200 bg-zinc-100 text-zinc-500"}
            data-testid="quiosco-estado-badge"
          >
            {kioskToken ? "Activo" : "Inactivo"}
          </Badge>
        }
      >
          {kioskToken ? (
            <>
              <div className="space-y-1.5">
                <Label>Enlace del quiosco web (PIN en tablet con navegador)</Label>
                <div className="flex gap-2">
                  <Input readOnly value={kioskUrl} className="font-mono text-xs" data-testid="quiosco-url-input" />
                  <Button variant="outline" size="icon" onClick={copiarUrl} data-testid="copiar-quiosco-btn" className="shrink-0 rounded-lg">
                    <Copy className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>

              <div className="rounded-xl border border-violet-100 bg-violet-50/40 p-5">
                <div className="flex items-start gap-4">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-2">
                      <QrCode className="h-4 w-4 text-violet-600" strokeWidth={1.5} />
                      <h3 className="text-sm font-medium text-zinc-900">App Android NFC</h3>
                    </div>
                    <p className="text-xs text-zinc-500">
                      Escanea el QR desde la app <b>Quiosco NFC</b>. Si la tablet no alcanza
                      <code className="mx-1 rounded bg-white px-1">localhost</code>, usa la IP del servidor en la red local.
                    </p>
                    <div className="space-y-1.5">
                      <Label htmlFor="kiosk-api-url">URL API para la app</Label>
                      <Input
                        id="kiosk-api-url"
                        value={kioskApiUrl}
                        onChange={(e) => setKioskApiUrl(e.target.value)}
                        placeholder="http://192.168.1.10:8083/api"
                        className="font-mono text-xs"
                        data-testid="quiosco-api-url-input"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Token de quiosco</Label>
                      <Input readOnly value={kioskToken} className="font-mono text-xs" data-testid="quiosco-token-input" />
                    </div>
                  </div>
                  <KioskQrCode apiUrl={kioskApiUrl} token={kioskToken} size={180} />
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={activarQuiosco} data-testid="regenerar-quiosco-btn" className="gap-1.5 rounded-lg">
                  <RefreshCw className="h-4 w-4" strokeWidth={1.5} />
                  Regenerar enlace
                </Button>
                <Button
                  variant="outline"
                  onClick={desactivarQuiosco}
                  data-testid="desactivar-quiosco-btn"
                  className="gap-1.5 rounded-lg border-red-200 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                  Desactivar
                </Button>
              </div>
            </>
          ) : (
            <Button onClick={activarQuiosco} data-testid="activar-quiosco-btn" className="rounded-lg bg-indigo-600 hover:bg-indigo-700">
              Activar modo quiosco
            </Button>
          )}
          <p className="text-xs text-zinc-400">
            Asigna un PIN a cada empleado desde <b>Empleados → Editar</b>. En el quiosco web solo hay que teclear el PIN.
            Para fichar con tarjeta NFC, usa la app Android y configúrala escaneando el QR de arriba.
          </p>
      </AjustesSeccion>

      <div className="space-y-4">
        {[
          {
            key: "incidencia",
            titulo: "Motivos de incidencias no justificadas",
            desc: "Catálogo para justificar incidencias fuera de horario. Incluye «Otros» para texto libre.",
            lista: motivosIncidencia,
            border: "border-rose-100",
            icon: "text-rose-600",
            btn: "bg-rose-600 hover:bg-rose-700",
          },
          {
            key: "ausencia_justificada",
            titulo: "Motivos de ausencias justificadas",
            desc: "Permisos y causas reconocidas (cita médica, familiares, matrimonio, mudanza…). Puedes añadir nuevas según convenio.",
            lista: motivosAusencia,
            border: "border-emerald-100",
            icon: "text-emerald-600",
            btn: "bg-emerald-600 hover:bg-emerald-700",
          },
          {
            key: "pausa",
            titulo: "Motivos de pausa en el trabajo",
            desc: "Motivos al iniciar una pausa (café, comida, fumar, personal…). El tiempo se rige por la «pausa reconocida» de arriba: el exceso se descuenta. Editable por la empresa.",
            lista: motivosPausa,
            border: "border-amber-100",
            icon: "text-amber-600",
            btn: "bg-amber-600 hover:bg-amber-700",
          },
        ].map((cat) => (
          <div key={cat.key} className={`rounded-xl border ${cat.border} bg-white p-5 shadow-sm`}>
            <div className="flex items-center gap-2">
              <Clock className={`h-5 w-5 ${cat.icon}`} />
              <h2 className="font-semibold text-zinc-900">{cat.titulo}</h2>
            </div>
            <p className="mt-1 text-sm text-zinc-500">{cat.desc}</p>
            <ul className="mt-4 space-y-2">
              {cat.lista.filter((m) => m.activo).map((m) => (
                <li key={m.id} className="flex items-center justify-between gap-2 rounded-lg border border-zinc-100 px-3 py-2 text-sm">
                  {editandoMotivo?.id === m.id ? (
                    <div className="flex flex-1 flex-wrap items-center gap-2">
                      <Input
                        className="max-w-xs"
                        value={editandoMotivo.nombre}
                        onChange={(e) => setEditandoMotivo({ ...editandoMotivo, nombre: e.target.value })}
                      />
                      <Button size="sm" className="h-8" onClick={guardarMotivoEdit}>Guardar</Button>
                      <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditandoMotivo(null)}>Cancelar</Button>
                    </div>
                  ) : (
                    <>
                      <span className="text-zinc-800">
                        {m.nombre}{m.es_otros ? " (texto libre)" : ""}
                      </span>
                      <div className="flex shrink-0 gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 text-zinc-600"
                          onClick={() => setEditandoMotivo({ id: m.id, nombre: m.nombre })}
                        >
                          Editar
                        </Button>
                        {!m.es_otros && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 text-red-600"
                            onClick={async () => {
                              await api.delete(`/motivos-incidencia/${m.id}`);
                              cargarMotivos();
                            }}
                          >
                            Desactivar
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </li>
              ))}
              {cat.lista.filter((m) => m.activo).length === 0 && (
                <li className="py-2 text-center text-xs text-zinc-400">Sin motivos. Añade el primero.</li>
              )}
            </ul>
            <div className="mt-3 flex gap-2">
              <Input
                placeholder="Nuevo motivo…"
                value={motivoNuevo[cat.key] || ""}
                onChange={(e) => setMotivoNuevo((prev) => ({ ...prev, [cat.key]: e.target.value }))}
              />
              <Button
                className={`shrink-0 gap-1 ${cat.btn}`}
                onClick={() => anadirMotivo(cat.key)}
              >
                <Plus className="h-4 w-4" /> Añadir
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
